/**
 * The screener's push subscription — one socket per screener section.
 *
 * `ScreenerView` used to `fetch` a screen per selection change. That is one
 * whole screen build per click, and it can only ever be as fresh as the last
 * click: a screen left open went stale silently, which is the failure the chart
 * has a reconnecting socket to avoid. This subscribes instead — the server sends
 * wave 1 on subscribe (first paint) and pushes again whenever the answer
 * changes, and each push is folded into what is on screen by
 * `applyScreenerPush`.
 *
 * ## The route is config, and unset means the old path
 *
 * `VITE_QV_SCREEN_URL` unset ⇒ this hook is inert and `ScreenerView` keeps
 * fetching. That is deliberate, and not just caution: the HTTP route works
 * today, the socket is new on both sides, and the two produce *the same named
 * frames* — so the merge path is exercised either way and turning the socket on
 * is a configuration change rather than a deploy that has to land in lockstep
 * with the server's. Every other optional service in this app degrades the same
 * way (`symbolsConfigured`, `VITE_QV_SET_URL`).
 *
 * ## The debounce belongs here, not in the socket
 *
 * Multi Select's tick list stays open while you pick, so choosing five groups is
 * five selection changes in a couple of seconds. Five subscribes is five screen
 * builds on a server that rations them; one is correct. The first subscribe and
 * an explicit Refresh go straight out — one has nothing before it to coalesce
 * with and the other is a request for *now* — which is exactly the rule the
 * fetch path already followed.
 */

import { useEffect, useRef } from "react";
import {
  ScreenerRosterClient,
  type ConnectionStatus,
  type ScreenerComputedRequest,
  type ScreenerRosterSnapshot,
  type ScreenerSealedConditionRequest,
  type TransportError,
} from "@qufolio/transport";

import { idToken } from "../lib/session";
import { currentPass } from "../lib/pass";
import { actingAs } from "../lib/user";
import { wsUrl } from "./backend";

/**
 * The roster socket, under whichever backend is selected.
 *
 * `wsUrl` derives the scheme from the HTTP override, because `https://` must
 * become `wss://` and a hand-typed `ws://` against an HTTPS origin is refused as
 * mixed content with an error naming neither the scheme nor the setting.
 */
const SCREEN_WS_URL = wsUrl("/sym/screen2/ws", import.meta.env.VITE_QV_SCREEN_URL);

/** True when a socket is configured — `ScreenerView` fetches otherwise. */
export const screenStreamConfigured = SCREEN_WS_URL !== "";

/**
 * How long a changed selection waits before it is subscribed. See the header.
 *
 * The same number and the same reason as `ScreenerView`'s
 * `SELECTION_DEBOUNCE_MS`, kept as its own constant because the two are
 * justified separately — a fetch also spends from qv-data's `HEAVY` budget,
 * which a subscribe does not — but they must not drift apart by accident.
 */
export const SUBSCRIBE_DEBOUNCE_MS = 300;

export interface ScreenStreamOptions {
  /** Group codes to screen. Empty ⇒ nothing is subscribed and no socket opens. */
  codes: readonly string[];
  mode: "union" | "intersect";
  /** The `needsHistory` columns — `serverComputedColumns`' output, verbatim. */
  computed: readonly ScreenerComputedRequest[];
  /** Ticked sealed conditions — `sealedConditions(config)`, verbatim. */
  sealedConditions?: readonly ScreenerSealedConditionRequest[];
  /**
   * The Strat stepper's month, `YYYY-MM`, for a one-strategy screen — omitted is
   * the newest month (docs/plans/strategy-lists.md phase 5). Must be in `key`.
   */
  strategyMonth?: string;
  /**
   * A value signature of the inputs above.
   *
   * The effect's only subscription dependency, for the same reason
   * `ScreenerView` keys its fetch on `codesKey`/`computedKey`: `computed` is a
   * fresh array on every config patch, so keying on identity would re-subscribe
   * on an unrelated column resize.
   */
  key: string;
  /** Bumped to re-send the subscription in force — the Refresh button. */
  refreshNonce?: number;
  onSnapshot: (snapshot: ScreenerRosterSnapshot) => void;
  onStatus?: (status: ConnectionStatus) => void;
  onError?: (error: TransportError) => void;
}

/**
 * Hold one subscription open for as long as the caller is mounted.
 *
 * Two effects, because the two lifetimes genuinely differ: the socket lives for
 * the component, and the subscription changes underneath it. **A selection
 * change is one text frame, not a reconnect** — that is the whole reason this is
 * a socket, since the server answers a re-subscribe from the frame it is already
 * holding.
 */
export function useScreenStream({
  codes,
  mode,
  computed,
  sealedConditions,
  strategyMonth,
  key,
  refreshNonce = 0,
  onSnapshot,
  onStatus,
  onError,
}: ScreenStreamOptions): void {
  // Held in refs and read at the moment of the call, so the socket is not torn
  // down when a parent re-renders with new callback identities. Same shape as
  // `ScreenerGrid`'s `trackRef`, and for the same reason.
  const listeners = useRef({ onSnapshot, onStatus, onError });
  listeners.current = { onSnapshot, onStatus, onError };

  const clientRef = useRef<ScreenerRosterClient | null>(null);
  /** Whether anything has been subscribed yet — the first one is not debounced. */
  const subscribed = useRef(false);

  useEffect(() => {
    if (!screenStreamConfigured) return;

    const client = new ScreenerRosterClient({
      url: SCREEN_WS_URL,
      authToken: idToken,
      pass: currentPass,
      actAs: actingAs,
      listeners: {
        onSnapshot: (snapshot) => listeners.current.onSnapshot(snapshot),
        onStatus: (status) => listeners.current.onStatus?.(status),
        onError: (error) => listeners.current.onError?.(error),
      },
    });
    clientRef.current = client;
    void client.connect().catch(() => {
      // A refused handshake reports itself through `onError` and then `onStatus`,
      // and the client's own backoff takes it from there. This catch exists only
      // so the rejection is not an unhandled one.
    });

    return () => {
      clientRef.current = null;
      subscribed.current = false;
      client.disconnect();
    };
  }, []);

  useEffect(() => {
    const client = clientRef.current;
    if (!client || codes.length === 0) return;

    const subscription = { codes, mode, computed, sealedConditions, ...(strategyMonth ? { strategyMonth } : {}) };
    // Recorded before the wait: `subscribe` is safe on a socket that is still
    // handshaking — it stores and `onopen` sends — so the first selection does
    // not have to wait for the connection either.
    if (!subscribed.current) {
      subscribed.current = true;
      client.subscribe(subscription);
      return;
    }

    const timer = setTimeout(() => client.subscribe(subscription), SUBSCRIBE_DEBOUNCE_MS);
    // The cancel *is* the debounce: the next tick re-runs this effect, and its
    // teardown drops the subscribe the previous one had queued.
    return () => clearTimeout(timer);
    // Keyed by value — see `key`. `codes`/`mode`/`computed` are read through the
    // closure and are exactly what `key` is a signature of.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Refresh: re-send what is already subscribed. The pinned protocol carries no
  // cache-dropping flag, so this asks again rather than forcing a recompute —
  // worth knowing before reading a refreshed number as a recomputed one.
  const seenRefresh = useRef(refreshNonce);
  useEffect(() => {
    if (refreshNonce === seenRefresh.current) return;
    seenRefresh.current = refreshNonce;
    clientRef.current?.resubscribe();
  }, [refreshNonce]);
}
